/**
 * RecommendationVariant — AI-recommended dish card.
 * Uses shared EngagementBar + ShareSection.
 * Unique: Google Places image, blurb (always visible), Google Reviews panel, service pills.
 */
import { useState, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, MapPin, ExternalLink,
  ChefHat, Youtube, ListPlus,
  Award, ThumbsUp, MessageCircle, Send,
  ChevronDown, Flame, Zap, TrendingUp
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useFoodlist } from '../../contexts/FoodlistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFoodImages } from '../../hooks/useFoodImage';
import { useEngagement } from '../../hooks/useEngagement';
import AuthModal from '../AuthModal';
import EngagementBar from './EngagementBar';
import ShareSection from './ShareSection';
import ImageCarousel from './ImageCarousel';
import { API_URL, buildShareHandler } from './shared';

const RecommendationVariant = ({ dish, mode = 'dine-out', onMetricRefresh }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isInFoodlist, toggleFoodlist } = useFoodlist();
  const { toast } = useToast();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [saveAnim, setSaveAnim] = useState(false);

  const cleanLocation = useMemo(() => {
    const loc = dish.location || dish.address || dish.restaurant || '';
    return loc.replace(/,?\s*(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/gi, '').replace(/,?\s*Australia$/i, '').trim();
  }, [dish.location, dish.address, dish.restaurant]);

  const primaryImg = dish.image || dish.photo_url || '';
  const { images: carouselImages } = useFoodImages(dish.name, primaryImg, dish.restaurant || '', cleanLocation, dish.cuisine || '');

  const { repostCount, reposted, shareCount, toggleRepost, recordShare } = useEngagement(
    dish.name || '', dish.restaurant || '', dish.id || '', user?.user_id || ''
  );
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(dish.likes_count || Math.floor(Math.random() * 30) + 2);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [claimed, setClaimed] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);

  const [showGoogleReviews, setShowGoogleReviews] = useState(false);
  const [googleReviews, setGoogleReviews] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [vibeDisliked, setVibeDisliked] = useState(false);
  const [foodlistCount, setFoodlistCount] = useState(dish.foodlist_count || Math.floor(Math.random() * 40) + 5);

  const testId = useMemo(() => (dish.id || dish.name || 'item').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-'), [dish.id, dish.name]);
  const inFoodlist = isInFoodlist(dish.name, dish.restaurant);
  const rating = dish.rating || dish.googleRating || 4.5;
  const priceSymbol = dish.priceRange || dish.price_symbol || '$$';
  const isOpen = dish.isOpen !== false;

  const distance = useMemo(() => {
    const dist = dish.distance_km || dish.distance;
    if (!dist) return null;
    const num = String(dist).replace(/\s*km\s*/gi, '').trim();
    return !num || isNaN(parseFloat(num)) ? null : `${num} km`;
  }, [dish.distance, dish.distance_km]);

  const fullAiText = dish.whyItFits || dish.flavorCaption || dish.description || '';

  /* ── Handlers ── */
  const handleFoodlistToggle = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleFoodlist(dish);
    if (!inFoodlist) { setSaveAnim(true); setFoodlistCount(c => c + 1); setTimeout(() => setSaveAnim(false), 800); }
    else { setFoodlistCount(c => Math.max(0, c - 1)); }
  }, [user, dish, toggleFoodlist, inFoodlist]);

  const shareUrl = `${window.location.origin}/recommendations?dish=${encodeURIComponent(dish.name || '')}`;
  const baseShareHandler = useMemo(() => buildShareHandler(dish.name, dish.restaurant, shareUrl), [dish.name, dish.restaurant, shareUrl]);
  const handleSocialShare = useCallback(async (platform) => {
    const result = await baseShareHandler(platform);
    recordShare(platform);
    if (platform === 'copy') {
      toast({ title: 'Link copied!', description: 'Share link copied to clipboard' });
    }
  }, [baseShareHandler, recordShare, toast]);

  const handleGoogleReviews = useCallback(async () => {
    setShowGoogleReviews(prev => !prev);
    if (googleReviews || !dish.place_id) return;
    setReviewsLoading(true);
    try { const res = await fetch(`${API_URL}/api/places/details/${dish.place_id}`); if (res.ok) setGoogleReviews(await res.json()); }
    catch {} finally { setReviewsLoading(false); }
  }, [dish.place_id, googleReviews]);

  const handleRepost = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleRepost();
  }, [user, toggleRepost]);

  const handleLike = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    if (!liked) { setLikes(l => l + 1); setLiked(true); }
    else { setLikes(l => Math.max(0, l - 1)); setLiked(false); }
  }, [user, liked]);

  const handleNotMyVibe = useCallback(async () => {
    if (!user) { setShowAuthModal(true); return; }
    setVibeDisliked(true);
    try {
      await fetch(`${API_URL}/api/preferences/not-my-vibe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ item_name: dish.name || '', cuisine: dish.cuisine || '', category: mode, reason: 'not_interested', source: 'surprise_me' }),
      });
    } catch {}
  }, [user, dish, mode]);

  const handleRepostClaim = useCallback(async () => {
    if (!user) { setShowAuthModal(true); return; }
    setClaimLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/claims`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ dish_name: dish.name || '', venue_name: dish.restaurant || cleanLocation || '', cuisine: dish.cuisine || '', mode: mode.replace('-', '_'), has_photo: carouselImages.some(i => i.type === 'photo') }),
      });
      if (res.ok) setClaimed(true);
    } catch {} finally { setClaimLoading(false); }
    if (!carouselImages.some(i => i.type === 'photo')) navigate('/create-foodcard', { state: { prefill: dish, claimMode: true } });
  }, [user, dish, mode, carouselImages, cleanLocation, navigate]);

  const handleFoodCardTap = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    navigate('/create-foodcard', { state: { prefill: dish, claimMode: true } });
  }, [user, dish, navigate]);

  const handleGetRecipe = useCallback(() => { window.open(`https://spoonacular.com/recipes/${encodeURIComponent(dish.name?.toLowerCase().replace(/\s+/g, '-') || 'pasta')}`, '_blank'); }, [dish.name]);
  const handleWatchVideo = useCallback(() => { window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(`${dish.name} recipe`)}`, '_blank'); }, [dish.name]);

  const [pillsOpen, setPillsOpen] = useState(false);
  const isPrepare = mode === 'cook-at-home' || mode === 'prepare-it';
  const isDineOut = mode === 'dine-out';
  const isOrderIn = mode === 'order-in' || mode === 'dine-in';

  const pillLabel = isDineOut ? 'Dine Out Options' : isOrderIn ? 'Order Delivery' : isPrepare ? 'Cook & Shop' : 'Actions';

  return (
    <>
      <article className="group bg-white rounded-[22px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300 relative" data-testid={`foodcard-${testId}`}>
        {/* Trending badge */}
        {dish.trending && (
          <div className="absolute top-4 left-4 z-10 flex items-center gap-1 bg-amber-500/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full" data-testid={`trending-${testId}`}>
            <TrendingUp className="w-3 h-3" /> Trending{dish.location ? ` in ${dish.location.split(',')[0]}` : ''}
          </div>
        )}
        {/* ── Image Carousel ── */}
        <ImageCarousel
          images={carouselImages}
          dishName={dish.name}
          restaurant={dish.restaurant}
          location={cleanLocation}
          rating={rating}
          priceSymbol={priceSymbol}
          distance={distance}
          isOpen={isOpen}
          cuisine={dish.cuisine}
          vibe={dish.vibe}
          claimed={claimed}
          onAddPhoto={() => {
            if (!user) { setShowAuthModal(true); return; }
            navigate('/create-foodcard', { state: { prefill: dish, claimMode: true } });
          }}
          testId={testId}
        />

        {/* ── Card Body ── */}
        <div className="p-4 md:p-5">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-0.5 line-clamp-1">{dish.name}</h3>
          {dish.restaurant && <p className="text-sm font-medium text-gray-600 mb-0.5 line-clamp-1">{dish.restaurant}</p>}
          {cleanLocation && (
            <p className="text-gray-400 text-xs mb-2 flex items-center gap-1.5 line-clamp-1"><MapPin className="w-3 h-3 flex-shrink-0" />{cleanLocation}</p>
          )}

          {/* Nutrition tags + dietary */}
          {(dish.estimated_calories || (dish.dietary_tags && dish.dietary_tags.length > 0)) && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid={`nutrition-${testId}`}>
              {dish.estimated_calories && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                  <Flame className="w-2.5 h-2.5" /> ~{dish.estimated_calories} kcal
                </span>
              )}
              {dish.protein_level && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  dish.protein_level === 'High' ? 'bg-blue-50 text-blue-700' :
                  dish.protein_level === 'Medium' ? 'bg-gray-100 text-gray-600' :
                  'bg-gray-50 text-gray-500'
                }`}>
                  <Zap className="w-2.5 h-2.5 inline mr-0.5" />{dish.protein_level} protein
                </span>
              )}
              {dish.dietary_tags?.map((tag, i) => (
                <span key={i} className="text-[10px] font-medium bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* AI Blurb — always visible, no animation */}
          {fullAiText && (
            <div className="rounded-2xl bg-gray-50 p-4 mb-3" data-testid={`blurb-${testId}`}>
              <p className="text-[15px] text-gray-600 leading-relaxed">{fullAiText}</p>
              {/* Likes, comments & quick chat */}
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-3">
                  <button onClick={handleLike} className={`flex items-center gap-1 text-sm transition-all ${liked ? 'text-blue-500' : 'text-gray-400 hover:text-blue-400'}`} data-testid={`blurb-like-${testId}`}>
                    <ThumbsUp className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} /><span className="font-medium">{likes} likes</span>
                  </button>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs text-gray-400">{comments.length} comments</span>
                </div>
                {comments.length > 0 && (
                  <div className="space-y-2 max-h-28 overflow-y-auto">
                    {comments.slice(-3).map((c, i) => (
                      <div key={i} className="flex items-start gap-2"><div className="w-5 h-5 rounded-full bg-gray-200 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700"><span className="font-semibold">You</span> {c.text}</p></div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="text" value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment..."
                    className="flex-1 px-3 py-2 bg-white rounded-xl text-sm border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/30"
                    data-testid={`blurb-comment-input-${testId}`}
                    onKeyDown={e => { if (e.key === 'Enter' && comment.trim()) { setComments(prev => [...prev, { text: comment }]); setComment(''); } }} />
                  <button onClick={() => { if (comment.trim()) { setComments(prev => [...prev, { text: comment }]); setComment(''); } }}
                    className="px-3 py-2 text-gray-400 hover:text-[#FF6B35] transition-colors" data-testid={`blurb-comment-post-${testId}`}><Send className="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          )}

          {/* Shared EngagementBar */}
          <EngagementBar repostCount={repostCount} onRepost={handleRepost} reposted={reposted}
            onNotMyVibe={handleNotMyVibe} vibeDisliked={vibeDisliked}
            inFoodlist={inFoodlist} foodlistCount={foodlistCount} onFoodlistToggle={handleFoodlistToggle} saveAnim={saveAnim}
            onFoodCardTap={handleFoodCardTap} claimed={claimed} claimLoading={claimLoading}
            testId={testId} />

          {/* Google Reviews Panel */}
          {showGoogleReviews && (
            <div className="mt-3 pt-3 border-t border-gray-100 bg-amber-50/50 rounded-2xl p-4" data-testid={`greviews-panel-${testId}`}>
              {reviewsLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500"><div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" /> Loading Google Reviews...</div>
              ) : googleReviews ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= Math.round(googleReviews.rating || rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />)}</div>
                    <span className="text-sm font-bold text-gray-900">{googleReviews.rating || rating}</span>
                    <span className="text-xs text-gray-500">({googleReviews.user_ratings_total || 0} reviews)</span>
                  </div>
                  {googleReviews.reviews?.slice(0, 2).map((r, i) => (
                    <div key={i} className="mb-2 last:mb-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="text-xs font-semibold text-gray-700">{r.author}</span>
                        <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-2.5 h-2.5 ${s <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />)}</div>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2">{r.text}</p>
                    </div>
                  ))}
                  <a href={`https://www.google.com/maps/place/?q=place_id:${dish.place_id}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 font-medium hover:underline">Review on Google <ExternalLink className="w-3 h-3" /></a>
                </>
              ) : (
                <div className="text-center py-2">
                  <div className="flex items-center justify-center gap-1 mb-1">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />)}</div>
                  <p className="text-sm font-bold text-gray-900">{rating} avg rating</p>
                  <a href={`https://www.google.com/search?q=${encodeURIComponent(`${dish.name} ${cleanLocation} reviews`)}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 font-medium hover:underline">See Google Reviews <ExternalLink className="w-3 h-3" /></a>
                </div>
              )}
            </div>
          )}

          {/* Shared ShareSection */}
          <ShareSection onSocialShare={handleSocialShare} shareCount={shareCount} testId={testId} />

          {/* Service Pills — collapsible */}
          <div className="mt-4 pt-3 border-t border-gray-100" data-testid={`service-pills-${testId}`}>
            <button
              onClick={() => setPillsOpen(prev => !prev)}
              className="flex items-center justify-between w-full group"
              data-testid={`pills-toggle-${testId}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center transition-colors group-hover:bg-gray-200">
                  {isDineOut && <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>}
                  {isOrderIn && <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>}
                  {isPrepare && <ChefHat className="w-4 h-4 text-gray-500" />}
                </div>
                <span className="text-xs font-semibold text-gray-500">{pillLabel}</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${pillsOpen ? 'rotate-180' : ''}`} />
            </button>

            <div className={`grid transition-all duration-250 ease-in-out ${pillsOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden">
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                  {isDineOut && (
                    <a href={`https://www.opentable.com/s?term=${encodeURIComponent(dish.restaurant || dish.name)}`} target="_blank" rel="noopener noreferrer"
                      className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all active:scale-95 bg-[#DA3743] text-white hover:opacity-90" data-testid={`pill-reserve-${testId}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                      Reserve Table
                    </a>
                  )}
                  {isOrderIn && (
                    <>
                      <a href={`https://www.ubereats.com/search?q=${encodeURIComponent(dish.name || '')}`} target="_blank" rel="noopener noreferrer"
                        className="shrink-0 flex items-center h-10 px-4 rounded-full transition-all active:scale-95 bg-[#142328] hover:opacity-90" data-testid={`pill-ubereats-${testId}`}
                        aria-label="Order on Uber Eats"><img src="/logos/ubereats-white.svg" alt="Uber Eats" className="h-[18px]" /></a>
                      <a href={`https://www.doordash.com/search/store/${encodeURIComponent(dish.name || '')}`} target="_blank" rel="noopener noreferrer"
                        className="shrink-0 flex items-center h-10 px-4 rounded-full transition-all active:scale-95 bg-white border border-gray-200 hover:border-gray-300" data-testid={`pill-doordash-${testId}`}
                        aria-label="Order on DoorDash"><img src="/logos/doordash.svg" alt="DoorDash" className="h-[18px]" /></a>
                    </>
                  )}
                  {isPrepare && (
                    <>
                      <button onClick={handleGetRecipe} className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all active:scale-95 bg-emerald-500 text-white hover:bg-emerald-600" data-testid={`pill-cook-${testId}`}><ChefHat className="w-3.5 h-3.5" />Cook This</button>
                      <a href={`https://www.woolworths.com.au/shop/search/products?searchTerm=${encodeURIComponent(dish.name || '')}`} target="_blank" rel="noopener noreferrer"
                        className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all active:scale-95 bg-[#125831] text-white hover:opacity-90" data-testid={`pill-grocery-${testId}`}><ListPlus className="w-3.5 h-3.5" />Grocery Cart</a>
                      <button onClick={handleWatchVideo} className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all active:scale-95 bg-[#FF0000] text-white hover:opacity-90" data-testid={`pill-video-${testId}`}><Youtube className="w-3.5 h-3.5" />Watch Recipe</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
};

const MemoizedRecommendationVariant = memo(RecommendationVariant);
MemoizedRecommendationVariant.displayName = 'RecommendationVariant';
export default MemoizedRecommendationVariant;
